/**
 * Chat Responses Data
 * Static responses for Mèo Học Bá chat feature
 * Strategy: Don't reveal products, just hint and guide students to CAT/THPT tests
 */

export interface ChatResponse {
  keywords: string[];
  responses: string[];
  action?: {
    type: 'navigate' | 'external' | 'none';
    url?: string;
    label?: string;
  };
}

// Greeting responses
const GREETINGS: ChatResponse = {
  keywords: ['xin chào', 'hello', 'hi', 'chào', 'hey', 'alo'],
  responses: [
    'Chào bạn! Mình là Mèo Học Bá 🎓\n\nBạn đang băn khoăn về nghề nghiệp tương lai? Mình có thể giúp bạn tìm ra câu trả lời!',
    'Meow~ Chào bạn! Bạn có biết mỗi người đều có những năng lực tiềm ẩn riêng không? 🌟',
    'Xin chào! Bạn muốn biết mình phù hợp với ngành gì nhất không? Mèo có cách giúp bạn! ✨',
  ],
  action: {
    type: 'navigate',
    url: '/huong-nghiep-cat',
    label: '🎯 Khám phá ngay',
  },
};

// CAT Test responses - tease without revealing details
const CAT_TEST: ChatResponse = {
  keywords: ['test', 'làm bài', 'đánh giá', 'trắc nghiệm', 'cat', 'năng lực'],
  responses: [
    'Bạn biết không, chỉ mất khoảng 15-20 phút là bạn có thể khám phá được 6 năng lực tiềm ẩn của mình! 🎯\n\nMuốn thử không?',
    'Mình có một bài trắc nghiệm khoa học có thể giúp bạn hiểu rõ bản thân hơn! Kết quả sẽ khiến bạn bất ngờ đó~ 🐱',
    'CAT test được thiết kế dựa trên mô hình Holland - đã giúp hàng triệu người tìm ra nghề phù hợp! Bạn muốn thử không? ✨',
  ],
  action: {
    type: 'navigate',
    url: '/huong-nghiep-cat?screen=instruction',
    label: '📝 Làm bài CAT ngay',
  },
};

// THPT Mock Exam
const THPT_EXAM: ChatResponse = {
  keywords: ['thi thử', 'thpt', 'tốt nghiệp', 'ôn thi', 'đề thi', 'luyện thi'],
  responses: [
    'Bạn đang chuẩn bị cho kỳ thi THPT? Mèo có những đề thi thử chất lượng cao giúp bạn đánh giá năng lực thực sự! 📚',
    'Muốn biết mình đang ở đâu so với mục tiêu? Thử làm đề thi thử của mèo xem sao! 💪',
    'Luyện đề là cách tốt nhất để tự tin trước kỳ thi! Mèo có sẵn những đề hay cho bạn~ 📝',
  ],
  action: {
    type: 'navigate',
    url: '/thu-vien-de-thi',
    label: '📖 Xem đề thi thử',
  },
};

// Holland Types - teaser only
const HOLLAND_GENERAL: ChatResponse = {
  keywords: [
    'holland',
    'riasec',
    'nhóm',
    'realistic',
    'investigative',
    'artistic',
    'social',
    'enterprising',
    'conventional',
  ],
  responses: [
    'Mô hình Holland chia con người thành 6 nhóm nghề nghiệp!\n\nMỗi người thường nổi trội ở 2-3 nhóm. Bạn thuộc nhóm nào? Làm bài CAT để biết nhé! 🎯',
    'R-I-A-S-E-C... 6 chữ cái này đại diện cho 6 kiểu người khác nhau. Bạn là kiểu nào? Chỉ cần 15 phút để tìm ra! 🌟',
    'Bạn có biết nghề nghiệp phù hợp phụ thuộc vào tính cách và năng lực của bạn không? Mô hình Holland sẽ giải đáp! ✨',
  ],
  action: {
    type: 'navigate',
    url: '/huong-nghiep-cat?screen=instruction',
    label: '🔍 Khám phá nhóm của bạn',
  },
};

// Majors - teaser without details
const MAJORS: ChatResponse = {
  keywords: [
    'ngành',
    'cntt',
    'y khoa',
    'kinh tế',
    'marketing',
    'kỹ thuật',
    'nghệ thuật',
    'thiết kế',
  ],
  responses: [
    'Có hàng trăm ngành học ngoài kia... Nhưng chỉ một vài ngành thực sự phù hợp với BẠN! 🎯\n\nMuốn biết đó là ngành nào không?',
    'Chọn ngành không chỉ dựa vào xu hướng, mà còn dựa vào năng lực và đam mê của bạn! Mèo có thể giúp bạn tìm ra~ 💡',
    'Mỗi ngành đòi hỏi những năng lực khác nhau. Bạn đã biết mình giỏi ở đâu chưa? 🌟',
  ],
  action: {
    type: 'navigate',
    url: '/huong-nghiep-cat?screen=instruction',
    label: '🎓 Tìm ngành phù hợp',
  },
};

// Career advice - teaser
const CAREER_ADVICE: ChatResponse = {
  keywords: ['nghề', 'tương lai', 'lương', 'việc làm', 'công việc', 'career', 'lời khuyên'],
  responses: [
    'Bạn đang mông lung về tương lai? Đừng lo, rất nhiều bạn cũng như vậy!\n\nNhưng mèo có cách giúp bạn nhìn rõ hơn đó~ 🐱',
    'Chọn nghề không khó nếu bạn hiểu rõ bản thân! Điều quan trọng là khám phá xem mình thực sự giỏi gì... ✨',
    'Tương lai bắt đầu từ việc hiểu bản thân! Bạn đã sẵn sàng khám phá chưa? 🌟',
  ],
  action: {
    type: 'navigate',
    url: '/huong-nghiep-cat',
    label: '🚀 Bắt đầu khám phá',
  },
};

// Questions about results
const RESULTS: ChatResponse = {
  keywords: ['kết quả', 'báo cáo', 'chi tiết', 'phân tích'],
  responses: [
    'Kết quả chi tiết sẽ cho bạn thấy bức tranh toàn diện về năng lực của mình! 📊\n\nHãy làm bài đánh giá trước nhé~',
    'Mèo có thể cho bạn phân tích chuyên sâu về năng lực... nhưng bạn cần làm bài test trước đã! 🎯',
    'Muốn có báo cáo riêng cho mình? Bước đầu tiên là hoàn thành bài đánh giá! ✨',
  ],
  action: {
    type: 'navigate',
    url: '/huong-nghiep-cat?screen=instruction',
    label: '📝 Làm bài đánh giá',
  },
};

// BIDV Bank Account Opening Guide
const BIDV_ACCOUNT: ChatResponse = {
  keywords: [
    'bidv',
    'mở tài khoản',
    'tài khoản ngân hàng',
    'ngân hàng',
    'thanh toán',
    'tài khoản thanh toán',
    'mở tk bidv',
    'bidv account',
    'nhận thưởng',
    '20k',
    'mã giảm giá',
  ],
  responses: [
    `Cognotiv Việt Nam kết hợp với Ngân hàng Đầu tư và Phát triển Việt Nam (BIDV) tặng mã giảm giá mở khóa báo cáo đầy đủ bài test Hướng nghiệp CAT, hoặc mở khóa bài thi thử tốt nghiệp THPT cho các bạn học sinh mở mới tài khoản ngân hàng tại BIDV! 🎁

Để nhận mã giảm giá, hãy làm theo các bước sau:

1️⃣ Làm bài test Hướng nghiệp CAT tại https://huongnghiep.cat.edu.vn
2️⃣ Click vào link hoặc quét QR để mở tài khoản BIDV online: https://dl.bidv.com.vn/MxnMIXTrah5
3️⃣ Nhập số tài khoản và tên tài khoản vừa mở tại BIDV vào form bên dưới
4️⃣ Chúng tôi sẽ kiểm tra thông tin và gửi thưởng cho bạn trong vòng tối đa 5 ngày làm việc
5️⃣ Sử dụng số dư hoặc mã giảm giá để mở khóa báo cáo đầy đủ hoặc làm bài thi thử THPT

Bạn đã sẵn sàng chưa? 🐱`,
    `Bạn muốn mở tài khoản BIDV để nhận 20k và mở khóa báo cáo Hướng nghiệp CAT? Mèo sẽ hướng dẫn bạn từng bước! 💰

📋 Quy trình đơn giản:
• Làm bài test CAT (nếu chưa làm)
• Mở tài khoản BIDV online qua link đặc biệt
• Điền form để nhận thưởng
• Nhận 20k trong tài khoản để mở khóa báo cáo!

Bạn có muốn bắt đầu không? 🚀`,
  ],
  action: {
    type: 'external',
    url: 'https://docs.google.com/forms/d/e/1FAIpQLSe9rTzHN1Ey0h-npptndXNpVGAf0dkqF80TPnPXCU5cGVYtew/viewform',
    label: '📝 Điền form nhận thưởng',
  },
};

// Default/fallback responses - always guide to tests
const DEFAULT_RESPONSES: ChatResponse = {
  keywords: [],
  responses: [
    'Hmm câu hỏi hay! 🤔\n\nNếu bạn muốn hiểu rõ hơn về bản thân và nghề nghiệp phù hợp, hãy thử làm bài CAT nhé!',
    'Mèo chưa hiểu lắm, nhưng mèo biết một điều: làm bài trắc nghiệm CAT sẽ giúp bạn tìm ra nhiều điều thú vị! 🐱',
    'Bạn có muốn khám phá năng lực tiềm ẩn của mình không? Chỉ mất 15 phút thôi! ✨',
  ],
  action: {
    type: 'navigate',
    url: '/huong-nghiep-cat',
    label: '🎯 Khám phá ngay',
  },
};

// All response categories
const ALL_RESPONSES: ChatResponse[] = [
  BIDV_ACCOUNT, // Check BIDV first as it's more specific
  GREETINGS,
  CAT_TEST,
  THPT_EXAM,
  HOLLAND_GENERAL,
  MAJORS,
  CAREER_ADVICE,
  RESULTS,
];

/**
 * Find the best matching response for a user message
 */
export function findResponse(message: string): {
  response: string;
  action?: ChatResponse['action'];
} {
  const lowerMessage = message.toLowerCase();

  for (const category of ALL_RESPONSES) {
    for (const keyword of category.keywords) {
      if (lowerMessage.includes(keyword)) {
        const response = category.responses[Math.floor(Math.random() * category.responses.length)];
        return { response, action: category.action };
      }
    }
  }

  // Default response if no match
  const response =
    DEFAULT_RESPONSES.responses[Math.floor(Math.random() * DEFAULT_RESPONSES.responses.length)];
  return { response, action: DEFAULT_RESPONSES.action };
}

/**
 * Get welcome message
 */
export function getWelcomeMessage(): string {
  return 'Chào bạn! Mình là Mèo Học Bá 🎓\n\nBạn có câu hỏi gì về:\n• Hướng nghiệp & chọn ngành\n• Năng lực bản thân\n• Thi thử THPT\n\nHỏi mình đi nào! 🐱';
}

/**
 * Quick action suggestions - lead to tests
 */
export const QUICK_ACTIONS = [
  { label: '💰 Mở tài khoản BIDV', message: 'Mình muốn mở tài khoản BIDV để nhận thưởng' },
  { label: '🎯 Khám phá năng lực', message: 'Mình muốn biết năng lực của mình' },
  { label: '📝 Thi thử THPT', message: 'Mình muốn thi thử THPT' },
  { label: '🔍 Tìm ngành phù hợp', message: 'Ngành nào phù hợp với mình?' },
  { label: '💡 Lời khuyên nghề nghiệp', message: 'Cho mình lời khuyên chọn nghề' },
];
